import React, { useState, useRef } from 'react';
import config from '../../config';
import { GlassChip } from './GlassChip';
import { GlassSpinner } from './GlassSpinner';
import './ImageUploadSection.css';
import './glass-tokens.css';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Image Thumbnail Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ImageThumb = ({ id, onRemove, isUploading = false }) => {
  const [imageError, setImageError] = useState(false);
  
  const imageUrl = `${config.API_BASE_URL}/gallery/download/${id}?isDownload=false`;

  return (
    <div className="image-thumb-wrapper">
      <div className="image-thumb">
        {isUploading ? (
          <div className="image-thumb-loading">
            <GlassSpinner size={24} />
          </div>
        ) : imageError ? (
          <div className="image-thumb-error">
            <span className="error-icon">🖼️</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={`Upload ${id}`}
            className="image-thumb-img"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        )}
        {isUploading && (
          <div className="image-thumb-overlay">
            <span className="uploading-text">Uploading...</span>
          </div>
        )}
      </div>
      <button 
        type="button"
        className="image-thumb-remove"
        onClick={() => onRemove(id)}
        aria-label="Remove image"
      >
        <i className="pi pi-times" style={{ fontSize: '10px' }}></i>
      </button>
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Add Image Button Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const AddImageButton = ({ onUpload, isUploading, disabled = false }) => {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    if (!isUploading && !disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e);
    }
    e.target.value = null;
  };

  return (
    <>
      <div 
        className={`add-image-btn ${isUploading || disabled ? 'disabled' : ''}`}
        onClick={handleClick}
      >
        {isUploading ? (
          <GlassSpinner size={24} />
        ) : (
          <>
            <span className="add-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="camera-icon">
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                <circle cx="12" cy="13" r="3"/>
              </svg>
            </span>
            <span className="add-label">Add</span>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden-file-input"
        onChange={handleFileChange}
        disabled={isUploading || disabled}
      />
    </>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main ImageUploadSection Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const ImageUploadSection = ({
  existingImageIds = [],
  uploadedImageIds = [],
  onImageUpload,
  onRemoveImage,
  isUploading = false,
  label = "Images",
  required = false,
  error = null,
  maxImages = 10,
  className = '',
}) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const allImages = [...existingImageIds, ...uploadedImageIds];
  const isAtMax = allImages.length >= maxImages;

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (isAtMax) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }

    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      const event = { target: { files } };
      onImageUpload(event);
    }
  };

  const handleFileInput = (e) => {
    if (isAtMax) {
      alert(`Maximum ${maxImages} images allowed`);
      e.target.value = null;
      return;
    }
    onImageUpload(e);
  };

  const handleRemove = (id) => {
    const isExisting = existingImageIds.includes(id);
    onRemoveImage(id, isExisting);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      if (allImages.length + imageFiles.length > maxImages) {
        alert(`Maximum ${maxImages} images allowed`);
        return;
      }
      const event = { target: { files: imageFiles } };
      onImageUpload(event);
    }
  };

  // ─── Render ──────────────────────────────────────────────────

  return (
    <div 
      className={`image-upload-section ${className}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      <div className="image-upload-header">
        <label className="section-label">
          {label}
          {required && <span className="required-star">*</span>}
          {allImages.length > 0 && (
            <span className="image-count">
              ({allImages.length}/{maxImages})
            </span>
          )}
        </label>
      </div>

      {error && <div className="image-upload-error">{error}</div>}

      {allImages.length > 0 && (
        <div className={`image-grid ${dragOver ? 'drag-over' : ''}`}>
          {allImages.map((id, index) => (
            <ImageThumb
              key={`${id}-${index}`}
              id={id}
              onRemove={handleRemove}
              isUploading={isUploading && index === allImages.length - 1}
            />
          ))}

          {!isAtMax && (
            <AddImageButton
              onUpload={handleFileInput}
              isUploading={isUploading}
              disabled={isAtMax}
            />
          )}
        </div>
      )}

      {allImages.length === 0 && !isUploading && (
        <div className="image-upload-placeholder">
          <div className="placeholder-content">
            <span className="placeholder-icon">🖼️</span>
            <span className="placeholder-text">
              Drop images here or click to upload
            </span>
            <span className="placeholder-hint">
              Supports JPG, PNG, GIF • Max {maxImages} images
            </span>
            <button 
              type="button"
              className="upload-trigger-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose Images
            </button>
          </div>
        </div>
      )}


      {isAtMax && allImages.length > 0 && (
        <div className="image-max-warning">
          <span>⚠️ Maximum {maxImages} images reached</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden-file-input"
        onChange={handleFileInput}
        disabled={isUploading || isAtMax}
      />
    </div>
  );
};

export default ImageUploadSection;